import { HttpClient } from '@angular/common/http';
import {
  NotificationService,
  OuiBoxComponent,
  OuiButtonComponent,
  OuiCenterComponent,
  OuiRowComponent,
  OuiStackComponent,
  OuiSymbolComponent,
  OuiTextComponent,
} from '@aiz-gl/omni-ui';
import {
  Component,
  computed,
  effect,
  inject,
  Input,
  signal,
  WritableSignal,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { RouterLink } from '@angular/router';
import { css } from '@emotion/css';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  finalize,
  map,
  Observable,
  of,
  Subscription,
  switchMap,
  tap,
} from 'rxjs';
import { CheckboxComponent } from '../../components/checkbox/checkbox.component';
import { SelectComponent } from '../../components/select/select.component';
import { Option } from '../../models/common.models';
import {
  RmaChannelConfig,
  RmaConfigurationResponse,
  UpdateRmaConfigurationRequest,
} from '../../models/warehouse.models';
import { NavigationMenuService } from '../../services/navigation-menu/navigation-menu.service';
import { SalesChannelService } from '../../services/sales-channel/sales-channel.service';
import { WarehouseService } from '../../services/warehouse/warehouse.service';
import { BasePageComponent } from '../base/base.component';

type ApproverUser = {
  email: string;
  firstName: string;
  lastName: string;
  rmaApproverUserId: number | null;
};

type ApproverRowSearchState = {
  searchText: WritableSignal<string>;
  results: WritableSignal<ApproverUser[]>;
  isLoading: WritableSignal<boolean>;
  queriedTerm: WritableSignal<string>;
  searchSubscription: Subscription;
  cache: Map<string, ApproverUser[]>;
};

type CreatorUser = ApproverUser;
type CreatorRowSearchState = ApproverRowSearchState;

@Component({
  selector: 'app-rma-config',
  standalone: true,
  imports: [
    OuiBoxComponent,
    OuiButtonComponent,
    OuiCenterComponent,
    OuiRowComponent,
    OuiStackComponent,
    OuiSymbolComponent,
    OuiTextComponent,
    SelectComponent,
    CheckboxComponent,
    ReactiveFormsModule,
    RouterLink,
  ],
  templateUrl: './rma-config.component.html',
})
export class RmaConfigPage extends BasePageComponent {
  private readonly warehouseService = inject(WarehouseService);
  private readonly salesChannelService = inject(SalesChannelService);
  private readonly http = inject(HttpClient);
  private readonly notification = inject(NotificationService);
  private readonly navigationMenuService = inject(NavigationMenuService);

  private readonly currentWarehouseCode = signal('');
  readonly isInitialPageLoading = signal(true);
  private hasLoadedInitialPageData = false;
  readonly warehouseName = signal('');

  private readonly rmaConfigs = signal<RmaChannelConfig[]>([]);

  readonly salesChannelOptions = computed<Option[]>(() =>
    this.salesChannelService.salesChannelOptions(),
  );

  get warehouseCode(): string {
    return this.currentWarehouseCode();
  }

  @Input()
  set warehouseCode(code: string) {
    if (!code) return;

    if (!this.hasLoadedInitialPageData) {
      this.isInitialPageLoading.set(true);
    }

    this.currentWarehouseCode.set(code);
    this.warehouseService.getWarehouseById(code).subscribe((w) => {
      this.warehouseName.set(w.name);
    });

    this.approversByChannel.clear();
    this.creatorsByChannel.clear();
    this.loadedApproverChannel = '';
    this.loadedCreatorChannel = '';

    this.warehouseService
      .getRmaChannelConfigs(code)
      .pipe(
        switchMap((configs) => {
          this.rmaConfigs.set(configs);

          const channelControl = this.channelForm.get('channelCode');
          const currentSelection = this.resolveChannelCode(channelControl?.value ?? '');
          const channelOptionValues = new Set(
            this.salesChannelOptions().map((option) => option.value),
          );
          const hasCurrentSelection = channelOptionValues.has(currentSelection);
          const firstAvailableChannel =
            configs[0]?.channelCode ?? this.salesChannelOptions()[0]?.value ?? '';
          const selectedChannelCode =
            hasCurrentSelection && currentSelection
              ? currentSelection
              : firstAvailableChannel;

          channelControl?.setValue(selectedChannelCode, { emitEvent: false });

          if (!selectedChannelCode) {
            return of({ selectedChannelCode: '' as string, configuration: null as RmaConfigurationResponse | null });
          }

          return this.warehouseService
            .getRmaConfiguration(code, selectedChannelCode)
            .pipe(
              map((configuration) => ({ selectedChannelCode, configuration })),
              catchError(() =>
                of({
                  selectedChannelCode,
                  configuration: {
                    accept_rma: false,
                    accepted_rma_types: '',
                    auto_approve_rma: false,
                    rma_approvers: [],
                    rma_creators: [],
                  } as RmaConfigurationResponse,
                }),
              ),
            );
        }),
      )
      .subscribe({
        next: ({ selectedChannelCode, configuration }) => {
          if (configuration && selectedChannelCode) {
            this.addChannelConfiguration(selectedChannelCode, configuration);
          }
          this.applyChannelConfig(selectedChannelCode);
          this.hasLoadedInitialPageData = true;
          this.isInitialPageLoading.set(false);
        },
        error: () => {
          this.hasLoadedInitialPageData = true;
          this.isInitialPageLoading.set(false);
        },
      });
  }

  private addChannelConfiguration(channelCode: string, configuration: RmaConfigurationResponse) {
    const approvers = (configuration.rma_approvers || []).map((user) => ({
      email: user.email || '',
      firstName: user.first_name || '',
      lastName: user.last_name || '',
      rmaApproverUserId: user.user_id ?? null,
    }));

    const creators = (configuration.rma_creators || []).map((user) => ({
      email: user.email || '',
      firstName: user.first_name || '',
      lastName: user.last_name || '',
      rmaApproverUserId: user.user_id ?? null,
    }));

    this.approversByChannel.set(channelCode, approvers);
    this.creatorsByChannel.set(channelCode, creators);
  }
  readonly channelForm = new FormGroup({
    channelCode: new FormControl(''),
  });

  readonly rmaSettingsForm = new FormGroup({
    enableChannelForRma: new FormControl(false),
    autoApproveRma: new FormControl(false),
    isRegular: new FormControl(false),
    isCreditOnly: new FormControl(false),
  });

  readonly approversFormArray = new FormArray<FormGroup>([]);
  readonly creatorsFormArray = new FormArray<FormGroup>([]);
  private approverRowSearchStates: ApproverRowSearchState[] = [];
  private creatorRowSearchStates: CreatorRowSearchState[] = [];
  private readonly openSuggestionIndex = signal<number | null>(null);
  private readonly openCreatorSuggestionIndex = signal<number | null>(null);

  /** Holds the approver rows for each channel so switching channels keeps them isolated. */
  private readonly approversByChannel = new Map<string, ApproverUser[]>();
  private readonly creatorsByChannel = new Map<string, CreatorUser[]>();
  /** Tracks which channel's approvers are currently loaded into the form array. */
  private loadedApproverChannel = '';
  /** Tracks which channel's creators are currently loaded into the form array. */
  private loadedCreatorChannel = '';

  /** Minimum characters before the user search endpoint is queried. */
  private static readonly MIN_SEARCH_LENGTH = 3;

  /** Exposes the minimum search length to the template. */
  get minSearchLength(): number {
    return RmaConfigPage.MIN_SEARCH_LENGTH;
  }

  get approvers(): FormGroup[] {
    return this.approversFormArray.controls as FormGroup[];
  }

  get creators(): FormGroup[] {
    return this.creatorsFormArray.controls as FormGroup[];
  }

  private normalizeUserId(value: unknown): number | null {
    const numericId = Number(value);
    return Number.isFinite(numericId) && numericId > 0 ? numericId : null;
  }

  /** Flags an email as invalid only when an earlier row already uses the same address. */
  private readonly duplicateApproverValidator = (
    control: AbstractControl,
  ): ValidationErrors | null => {
    const email = (control.value || '').trim().toLowerCase();
    if (!email) {
      return null;
    }

    const controls = this.approversFormArray.controls;
    const firstIndex = controls.findIndex((group) => {
      const value = ((group.get('email') as FormControl)?.value || '')
        .trim()
        .toLowerCase();
      return value === email;
    });
    const currentIndex = controls.findIndex(
      (group) => group.get('email') === control,
    );

    const existsInCreators = this.creatorsFormArray.controls.some((group) => {
      const value = ((group.get('email') as FormControl)?.value || '')
        .trim()
        .toLowerCase();
      return value === email;
    });

    if (existsInCreators) {
      return { alreadyCreator: true };
    }

    // Only the later occurrence(s) are marked invalid; the original stays clean.
    return firstIndex !== -1 && currentIndex > firstIndex
      ? { duplicateApprover: true }
      : null;
  };

  private revalidateApproverEmails() {
    this.approversFormArray.controls.forEach((group) => {
      group.get('email')?.updateValueAndValidity({ emitEvent: false });
    });

    this.creatorsFormArray.controls.forEach((group) => {
      group.get('email')?.updateValueAndValidity({ emitEvent: false });
    });
  }

  private readonly duplicateCreatorValidator = (
    control: AbstractControl,
  ): ValidationErrors | null => {
    const email = (control.value || '').trim().toLowerCase();
    if (!email) {
      return null;
    }

    const controls = this.creatorsFormArray.controls;
    const firstIndex = controls.findIndex((group) => {
      const value = ((group.get('email') as FormControl)?.value || '')
        .trim()
        .toLowerCase();
      return value === email;
    });
    const currentIndex = controls.findIndex(
      (group) => group.get('email') === control,
    );

    const existsInApprovers = this.approversFormArray.controls.some((group) => {
      const value = ((group.get('email') as FormControl)?.value || '')
        .trim()
        .toLowerCase();
      return value === email;
    });

    if (existsInApprovers) {
      return { alreadyApprover: true };
    }

    return firstIndex !== -1 && currentIndex > firstIndex
      ? { duplicateCreator: true }
      : null;
  };

  private revalidateCreatorEmails() {
    this.creatorsFormArray.controls.forEach((group) => {
      group.get('email')?.updateValueAndValidity({ emitEvent: false });
    });

    this.approversFormArray.controls.forEach((group) => {
      group.get('email')?.updateValueAndValidity({ emitEvent: false });
    });
  }

  constructor() {
    super();

    effect(() => {
      const options = this.salesChannelOptions();
      if (options.length === 0) return;

      const channelControl = this.channelForm.get('channelCode');
      const currentValue = channelControl?.value ?? '';
      const isValidSelection = options.some((opt) => String(opt.value) === currentValue);

      if (!isValidSelection) {
        channelControl?.setValue(String(options[0].value));
      }
    });

    this.channelForm.get('channelCode')?.valueChanges.subscribe((selectedValue) => {
      const channelCode = this.resolveChannelCode(selectedValue ?? '');
      if (channelCode && channelCode !== selectedValue) {
        this.channelForm.get('channelCode')?.setValue(channelCode, { emitEvent: false });
      }

      if (!channelCode) {
        this.applyChannelConfig(channelCode);
        return;
      }

      // Use cached config if already loaded for this channel
      if (this.approversByChannel.has(channelCode)) {
        this.applyChannelConfig(channelCode);
        return;
      }

      const warehouseCode = this.currentWarehouseCode();
      if (!warehouseCode) {
        this.applyChannelConfig(channelCode);
        return;
      }

      this.warehouseService
        .getRmaConfiguration(warehouseCode, channelCode)
        .pipe(
          catchError(() =>
            of({
              accept_rma: false,
              accepted_rma_types: '',
              auto_approve_rma: false,
              rma_approvers: [],
              rma_creators: [],
            } as RmaConfigurationResponse),
          ),
        )
        .subscribe((configuration) => {
          this.addChannelConfiguration(channelCode, configuration);
          this.applyChannelConfig(channelCode);
        });
    });

    this.rmaSettingsForm.valueChanges.subscribe(() => {
      this.upsertCurrentChannelConfig();
    });
  }

  private resolveChannelCode(selectedValue: string) {
    if (!selectedValue) {
      return '';
    }

    const byCode = this.rmaConfigs().some((c) => c.channelCode === selectedValue);
    if (byCode) {
      return selectedValue;
    }

    const channel = this.salesChannelService
      .salesChannels
      .value()
      .find((c) => c.name === selectedValue);

    return channel?.code || '';
  }

  private applyChannelConfig(channelCode: string) {
    this.switchApproverChannel(channelCode);
    this.switchCreatorChannel(channelCode);

    const config = this.rmaConfigs().find((c) => c.channelCode === channelCode);
    if (!config) {
      this.rmaSettingsForm.patchValue({
        enableChannelForRma: false,
        autoApproveRma: false,
        isRegular: false,
        isCreditOnly: false,
      });
      return;
    }

    this.rmaSettingsForm.patchValue({
      enableChannelForRma: config.acceptRma,
      autoApproveRma: config.autoApproveRma,
      isRegular: config.isRegular,
      isCreditOnly: config.isCreditOnly,
    }, { emitEvent: false });
  }

  private upsertCurrentChannelConfig() {
    const selectedChannelCode = this.resolveChannelCode(
      this.channelForm.get('channelCode')?.value ?? '',
    );

    if (!selectedChannelCode) {
      return;
    }

    const values = this.rmaSettingsForm.getRawValue();

    this.rmaConfigs.update((configs) => {
      const nextConfig: RmaChannelConfig = {
        channelCode: selectedChannelCode,
        acceptRma: !!values.enableChannelForRma,
        autoApproveRma: !!values.autoApproveRma,
        isRegular: !!values.isRegular,
        isCreditOnly: !!values.isCreditOnly,
        inventoryAllocation: 0,
      };

      const existingIndex = configs.findIndex(
        (config) => config.channelCode === selectedChannelCode,
      );

      if (existingIndex === -1) {
        return [...configs, nextConfig];
      }

      return configs.map((config) =>
        config.channelCode === selectedChannelCode ? { ...config, ...nextConfig } : config,
      );
    });
  }

  private toAcceptedRmaTypes(config: RmaChannelConfig) {
    const types: string[] = [];

    if (config.isRegular) {
      types.push('Regular');
    }

    if (config.isCreditOnly) {
      types.push('Credit Only');
    }

    return types.join('|');
  }

  /** Persists the current rows, then loads the rows belonging to the target channel. */
  private switchApproverChannel(channelCode: string) {
    if (channelCode === this.loadedApproverChannel) {
      return;
    }

    this.persistCurrentApprovers();
    this.loadApproversForChannel(channelCode);
  }

  private switchCreatorChannel(channelCode: string) {
    if (channelCode === this.loadedCreatorChannel) {
      return;
    }

    this.persistCurrentCreators();
    this.loadCreatorsForChannel(channelCode);
  }

  /** Snapshots the current form rows into the channel that is currently loaded. */
  private persistCurrentApprovers() {
    if (!this.loadedApproverChannel) {
      return;
    }

    const approvers = this.approvers.map((group) => ({
      email: ((group.get('email') as FormControl)?.value || '').trim(),
      firstName: group.get('firstName')?.value || '',
      lastName: group.get('lastName')?.value || '',
      rmaApproverUserId: this.normalizeUserId(
        (group.get('rmaApproverUserId') as FormControl<number | null>)?.value,
      ),
    }));

    this.approversByChannel.set(this.loadedApproverChannel, approvers);
  }

  private persistCurrentCreators() {
    if (!this.loadedCreatorChannel) {
      return;
    }

    const creators = this.creators.map((group) => ({
      email: ((group.get('email') as FormControl)?.value || '').trim(),
      firstName: group.get('firstName')?.value || '',
      lastName: group.get('lastName')?.value || '',
      rmaApproverUserId: this.normalizeUserId(
        (group.get('rmaApproverUserId') as FormControl<number | null>)?.value,
      ),
    }));

    this.creatorsByChannel.set(this.loadedCreatorChannel, creators);
  }

  /** Rebuilds the form array from the stored approvers for the given channel. */
  private loadApproversForChannel(channelCode: string) {
    this.approverRowSearchStates.forEach((state) =>
      state.searchSubscription.unsubscribe(),
    );
    this.approverRowSearchStates = [];
    this.approversFormArray.clear();
    this.openSuggestionIndex.set(null);

    const storedApprovers = this.approversByChannel.get(channelCode) ?? [];
    storedApprovers.forEach((approver) => {
      const approverFormGroup = new FormGroup({
        email: new FormControl(approver.email, [
          Validators.required,
          Validators.email,
          this.duplicateApproverValidator,
        ]),
        firstName: new FormControl(approver.firstName),
        lastName: new FormControl(approver.lastName),
        rmaApproverUserId: new FormControl<number | null>(
          approver.rmaApproverUserId,
        ),
      });

      this.approversFormArray.push(approverFormGroup);
      this.setupApproverSearch(
        this.approversFormArray.length - 1,
        approverFormGroup,
      );
    });

    this.loadedApproverChannel = channelCode;
  }

  private loadCreatorsForChannel(channelCode: string) {
    this.creatorRowSearchStates.forEach((state) =>
      state.searchSubscription.unsubscribe(),
    );
    this.creatorRowSearchStates = [];
    this.creatorsFormArray.clear();
    this.openCreatorSuggestionIndex.set(null);

    const storedCreators = this.creatorsByChannel.get(channelCode) ?? [];
    storedCreators.forEach((creator) => {
      const creatorFormGroup = new FormGroup({
        email: new FormControl(creator.email, [
          Validators.required,
          Validators.email,
          this.duplicateCreatorValidator,
        ]),
        firstName: new FormControl(creator.firstName),
        lastName: new FormControl(creator.lastName),
        rmaApproverUserId: new FormControl<number | null>(
          creator.rmaApproverUserId,
        ),
      });

      this.creatorsFormArray.push(creatorFormGroup);
      this.setupCreatorSearch(
        this.creatorsFormArray.length - 1,
        creatorFormGroup,
      );
    });

    this.loadedCreatorChannel = channelCode;
  }

  addApprover() {
    const approverFormGroup = new FormGroup({
      email: new FormControl('', [
        Validators.required,
        Validators.email,
        this.duplicateApproverValidator,
      ]),
      firstName: new FormControl(''),
      lastName: new FormControl(''),
      rmaApproverUserId: new FormControl<number | null>(null),
    });

    this.approversFormArray.push(approverFormGroup);
    this.setupApproverSearch(this.approversFormArray.length - 1, approverFormGroup);
  }

  addCreator() {
    const creatorFormGroup = new FormGroup({
      email: new FormControl('', [
        Validators.required,
        Validators.email,
        this.duplicateCreatorValidator,
      ]),
      firstName: new FormControl(''),
      lastName: new FormControl(''),
      rmaApproverUserId: new FormControl<number | null>(null),
    });

    this.creatorsFormArray.push(creatorFormGroup);
    this.setupCreatorSearch(this.creatorsFormArray.length - 1, creatorFormGroup);
  }

  removeApprover(index: number) {
    const state = this.approverRowSearchStates[index];
    state?.searchSubscription.unsubscribe();

    this.approverRowSearchStates.splice(index, 1);
    this.approversFormArray.removeAt(index);
    this.openSuggestionIndex.set(null);
    this.revalidateApproverEmails();
  }

  removeCreator(index: number) {
    const state = this.creatorRowSearchStates[index];
    state?.searchSubscription.unsubscribe();

    this.creatorRowSearchStates.splice(index, 1);
    this.creatorsFormArray.removeAt(index);
    this.openCreatorSuggestionIndex.set(null);
    this.revalidateCreatorEmails();
  }

  approverSuggestions(index: number) {
    return this.approverRowSearchStates[index]?.results() || [];
  }

  isApproverSearchLoading(index: number) {
    return this.approverRowSearchStates[index]?.isLoading() ?? false;
  }

  isSuggestionsOpen(index: number) {
    return this.openSuggestionIndex() === index;
  }

  creatorSuggestions(index: number) {
    return this.creatorRowSearchStates[index]?.results() || [];
  }

  isCreatorSearchLoading(index: number) {
    return this.creatorRowSearchStates[index]?.isLoading() ?? false;
  }

  isCreatorSuggestionsOpen(index: number) {
    return this.openCreatorSuggestionIndex() === index;
  }

  shouldShowNoApproverResults(index: number) {
    const state = this.approverRowSearchStates[index];
    if (!state) {
      return false;
    }

    const term = state.searchText().trim();
    return (
      term.length > 0 &&
      state.queriedTerm() === term &&
      !state.isLoading() &&
      state.results().length === 0
    );
  }

  shouldShowMinCharHint(index: number) {
    const state = this.approverRowSearchStates[index];
    if (!state) {
      return false;
    }

    const term = state.searchText().trim();
    return term.length > 0 && term.length < RmaConfigPage.MIN_SEARCH_LENGTH;
  }

  shouldShowNoCreatorResults(index: number) {
    const state = this.creatorRowSearchStates[index];
    if (!state) {
      return false;
    }

    const term = state.searchText().trim();
    return (
      term.length > 0 &&
      state.queriedTerm() === term &&
      !state.isLoading() &&
      state.results().length === 0
    );
  }

  shouldShowCreatorMinCharHint(index: number) {
    const state = this.creatorRowSearchStates[index];
    if (!state) {
      return false;
    }

    const term = state.searchText().trim();
    return term.length > 0 && term.length < RmaConfigPage.MIN_SEARCH_LENGTH;
  }

  onApproverEmailInput(index: number) {
    this.openSuggestionIndex.set(index);
  }

  onApproverEmailFocus(index: number) {
    this.openSuggestionIndex.set(index);
  }

  onApproverEmailBlur(index: number) {
    // Delay so a suggestion mousedown can register before the dropdown closes.
    setTimeout(() => {
      if (this.openSuggestionIndex() === index) {
        this.openSuggestionIndex.set(null);
      }
    }, 150);
  }

  onCreatorEmailInput(index: number) {
    this.openCreatorSuggestionIndex.set(index);
  }

  onCreatorEmailFocus(index: number) {
    this.openCreatorSuggestionIndex.set(index);
  }

  onCreatorEmailBlur(index: number) {
    setTimeout(() => {
      if (this.openCreatorSuggestionIndex() === index) {
        this.openCreatorSuggestionIndex.set(null);
      }
    }, 150);
  }

  selectApproverSuggestion(
    index: number,
    user: {
      email: string;
      firstName: string;
      lastName: string;
      rmaApproverUserId: number | null;
    },
  ) {
    const approver = this.approvers[index];
    if (!approver) {
      return;
    }

    // emitEvent: false prevents re-triggering a search for the selected email.
    approver.patchValue(
      {
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        rmaApproverUserId: user.rmaApproverUserId,
      },
      { emitEvent: false },
    );

    const state = this.approverRowSearchStates[index];
    if (state) {
      state.searchText.set(user.email);
      state.queriedTerm.set(user.email);
    }

    this.revalidateApproverEmails();
    this.openSuggestionIndex.set(null);
  }

  selectCreatorSuggestion(
    index: number,
    user: {
      email: string;
      firstName: string;
      lastName: string;
      rmaApproverUserId: number | null;
    },
  ) {
    const creator = this.creators[index];
    if (!creator) {
      return;
    }

    creator.patchValue(
      {
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        rmaApproverUserId: user.rmaApproverUserId,
      },
      { emitEvent: false },
    );

    const state = this.creatorRowSearchStates[index];
    if (state) {
      state.searchText.set(user.email);
      state.queriedTerm.set(user.email);
    }

    this.revalidateCreatorEmails();
    this.openCreatorSuggestionIndex.set(null);
  }

  private setupApproverSearch(index: number, approverFormGroup: FormGroup) {
    const emailControl = approverFormGroup.get('email') as FormControl<string | null>;
    const searchText = signal((emailControl.value || '').trim());
    const results = signal<ApproverUser[]>([]);
    const isLoading = signal(false);
    const queriedTerm = signal('');
    // Caches results per search term so re-typing a previously searched term
    // (e.g. after backspacing) is served from memory instead of the network.
    const cache = new Map<string, ApproverUser[]>();

    const searchSubscription = emailControl.valueChanges
      .pipe(
        map((value) => (value || '').trim()),
        // Track the latest typed value immediately (used for the "No users found" state)
        // and keep duplicate detection in sync across rows.
        tap((term) => {
          searchText.set(term);
          this.revalidateApproverEmails();
        }),
        // Only fire once typing has stopped for 600ms.
        debounceTime(600),
        distinctUntilChanged(),
        tap((term) => {
          // Skip the API call until the term is meaningful. This avoids
          // wasted hits from slow typers whose keystroke gaps exceed the
          // debounce window (e.g. searching "v", then "va", then "vas").
          if (term.length < RmaConfigPage.MIN_SEARCH_LENGTH) {
            results.set([]);
            queriedTerm.set('');
            isLoading.set(false);
            if (!term) {
              this.clearApproverNameFields(index);
            }
          } else if (!cache.has(term)) {
            isLoading.set(true);
          }
        }),
        // switchMap cancels any in-flight request when a newer term arrives,
        // so only the latest search ever resolves.
        switchMap((term) => {
          if (term.length < RmaConfigPage.MIN_SEARCH_LENGTH) {
            return of({ term: '', users: [] as ApproverUser[] });
          }

          const cached = cache.get(term);
          if (cached) {
            return of({ term, users: cached });
          }

          return this.searchUsers$(term, 10).pipe(
            map((users) => ({
              term,
              users: users.map((user) => ({
                email: user.email,
                firstName: user.firstName,
                lastName: user.lastName,
                rmaApproverUserId: this.normalizeUserId(user.userId),
              })),
            })),
            catchError(() => of({ term, users: [] as ApproverUser[] })),
          );
        }),
      )
      .subscribe(({ term, users }) => {
        if (!term) {
          return;
        }

        cache.set(term, users);
        results.set(users);
        isLoading.set(false);
        queriedTerm.set(term);
      });

    this.approverRowSearchStates[index] = {
      searchText,
      results,
      isLoading,
      queriedTerm,
      searchSubscription,
      cache,
    };
  }

  private setupCreatorSearch(index: number, creatorFormGroup: FormGroup) {
    const emailControl = creatorFormGroup.get('email') as FormControl<string | null>;
    const searchText = signal((emailControl.value || '').trim());
    const results = signal<CreatorUser[]>([]);
    const isLoading = signal(false);
    const queriedTerm = signal('');
    const cache = new Map<string, CreatorUser[]>();

    const searchSubscription = emailControl.valueChanges
      .pipe(
        map((value) => (value || '').trim()),
        tap((term) => {
          searchText.set(term);
          this.revalidateCreatorEmails();
        }),
        debounceTime(600),
        distinctUntilChanged(),
        tap((term) => {
          if (term.length < RmaConfigPage.MIN_SEARCH_LENGTH) {
            results.set([]);
            queriedTerm.set('');
            isLoading.set(false);
            if (!term) {
              this.clearCreatorNameFields(index);
            }
          } else if (!cache.has(term)) {
            isLoading.set(true);
          }
        }),
        switchMap((term) => {
          if (term.length < RmaConfigPage.MIN_SEARCH_LENGTH) {
            return of({ term: '', users: [] as CreatorUser[] });
          }

          const cached = cache.get(term);
          if (cached) {
            return of({ term, users: cached });
          }

          return this.searchUsers$(term, 10).pipe(
            map((users) => ({
              term,
              users: users.map((user) => ({
                email: user.email,
                firstName: user.firstName,
                lastName: user.lastName,
                rmaApproverUserId: this.normalizeUserId(user.userId),
              })),
            })),
            catchError(() => of({ term, users: [] as CreatorUser[] })),
          );
        }),
      )
      .subscribe(({ term, users }) => {
        if (!term) {
          return;
        }

        cache.set(term, users);
        results.set(users);
        isLoading.set(false);
        queriedTerm.set(term);
      });

    this.creatorRowSearchStates[index] = {
      searchText,
      results,
      isLoading,
      queriedTerm,
      searchSubscription,
      cache,
    };
  }

  private searchUsers$(term: string, limit: number): Observable<{ email: string; firstName: string; lastName: string; userId: number | null }[]> {
    const payload = {
      pageNumber: 0,
      pageSize: limit,
      sortBy: 'firstName',
      sortDirection: 'asc',
      firstName: term,
      lastName: term,
      email: term,
    };

    return this.http
      .post<Array<{ user_id?: number; id?: number; first_name?: string; last_name?: string; email?: string }> | { items?: unknown[]; data?: unknown[] }>('user/search', payload)
      .pipe(
        map((response) => {
          const rows = Array.isArray(response)
            ? response
            : ((response as { items?: unknown[]; data?: unknown[] }).items || (response as { items?: unknown[]; data?: unknown[] }).data || []) as Array<{ user_id?: number; id?: number; first_name?: string; last_name?: string; email?: string }>;
          return rows.map((r) => ({
            email: r.email || '',
            firstName: r.first_name || '',
            lastName: r.last_name || '',
            userId: r.user_id ?? r.id ?? null,
          }));
        }),
        catchError(() => of([])),
      );
  }

  private clearApproverNameFields(index: number) {
    const approver = this.approvers[index];
    if (!approver) {
      return;
    }

    approver.patchValue(
      {
        firstName: '',
        lastName: '',
        rmaApproverUserId: null,
      },
      { emitEvent: false },
    );
  }

  private clearCreatorNameFields(index: number) {
    const creator = this.creators[index];
    if (!creator) {
      return;
    }

    creator.patchValue(
      {
        firstName: '',
        lastName: '',
        rmaApproverUserId: null,
      },
      { emitEvent: false },
    );
  }

  private buildUnifiedRmaPayload(
    channelCode: string,
  ): UpdateRmaConfigurationRequest {
    const config = this.rmaConfigs().find((item) => item.channelCode === channelCode);
    if (!config) {
      throw new Error('Please select a valid channel before saving.');
    }

    const approvers = this.approvers.map((group) => ({
      email: ((group.get('email') as FormControl)?.value || '').trim(),
      firstName: group.get('firstName')?.value || '',
      lastName: group.get('lastName')?.value || '',
      rmaApproverUserId: this.normalizeUserId(
        (group.get('rmaApproverUserId') as FormControl<number | null>)?.value,
      ),
    }));

    const creators = this.creators.map((group) => ({
      email: ((group.get('email') as FormControl)?.value || '').trim(),
      firstName: group.get('firstName')?.value || '',
      lastName: group.get('lastName')?.value || '',
      rmaApproverUserId: this.normalizeUserId(
        (group.get('rmaApproverUserId') as FormControl<number | null>)?.value,
      ),
    }));

    const approverIds = approvers
      .map((approver) => this.normalizeUserId(approver.rmaApproverUserId))
      .filter((id): id is number => id !== null);

    const creatorIds = creators
      .map((creator) => this.normalizeUserId(creator.rmaApproverUserId))
      .filter((id): id is number => id !== null);

    const hasInvalidApprover = approvers.some(
      (approver) =>
        !!approver.email &&
        this.normalizeUserId(approver.rmaApproverUserId) === null,
    );
    const hasInvalidCreator = creators.some(
      (creator) =>
        !!creator.email &&
        this.normalizeUserId(creator.rmaApproverUserId) === null,
    );

    if (hasInvalidApprover) {
      throw new Error('Please select valid RMA approvers from the suggestions list.');
    }

    if (hasInvalidCreator) {
      throw new Error('Please select valid RMA creators from the suggestions list.');
    }

    return {
      accept_rma: config.acceptRma,
      accepted_rma_types: this.toAcceptedRmaTypes(config),
      auto_approve_rma: config.autoApproveRma,
      rma_approvers: approverIds,
      rma_creators: creatorIds,
    };
  }

  isSaving = false;

  cancel() {
    this.router.navigate(['/locations/warehouses', this.currentWarehouseCode()]);
  }

  save() {
    this.upsertCurrentChannelConfig();
    this.persistCurrentApprovers();
    this.persistCurrentCreators();

    this.approversFormArray.markAllAsTouched();
    this.creatorsFormArray.markAllAsTouched();
    if (this.approversFormArray.invalid || this.creatorsFormArray.invalid || this.isSaving) return;

    const warehouseCode = this.currentWarehouseCode();
    if (!warehouseCode) return;

    const selectedChannelCode = this.resolveChannelCode(
      this.channelForm.get('channelCode')?.value ?? '',
    );
    if (!selectedChannelCode) {
      this.notification.showSnackbar({
        message: 'Please select a channel before saving.',
        variant: 'warn',
        symbolName: 'error',
      });
      return;
    }

    this.isSaving = true;

    let unifiedPayload: UpdateRmaConfigurationRequest;
    try {
      unifiedPayload = this.buildUnifiedRmaPayload(selectedChannelCode);
    } catch (error) {
      this.isSaving = false;
      this.notification.showSnackbar({
        message:
          error instanceof Error
            ? error.message
            : 'Failed to prepare RMA configuration payload',
        variant: 'warn',
        symbolName: 'error',
      });
      return;
    }

    this.warehouseService
      .updateRmaConfiguration(
        warehouseCode,
        selectedChannelCode,
        unifiedPayload,
      )
      .pipe(
        finalize(() => {
          this.isSaving = false;
        }),
      )
      .subscribe({
        next: () => {
          this.notification.showSnackbar({
            message: 'RMA configuration saved successfully',
            variant: 'success',
            symbolName: 'check_circle',
          });
          void this.navigationMenuService.refreshPendingApprovalCount();
        },
        error: (err) => {
          const errorMessage =
            err?.error?.message ||
            err?.message ||
            'Failed to save RMA configuration';
          this.notification.showSnackbar({
            message: errorMessage,
            variant: 'warn',
            symbolName: 'error',
          });
        },
      });
  }

  // ---- Styles ----

  readonly toggleClass = css({
    position: 'relative',
    display: 'inline-block',
    width: '44px',
    height: '24px',
    '& input': { opacity: 0, width: 0, height: 0, position: 'absolute' },
    '& span': {
      position: 'absolute',
      cursor: 'pointer',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: '#ccc',
      borderRadius: '24px',
      transition: '0.3s',
    },
    '& span::before': {
      position: 'absolute',
      content: '""',
      height: '18px',
      width: '18px',
      left: '3px',
      bottom: '3px',
      backgroundColor: 'white',
      borderRadius: '50%',
      transition: '0.3s',
    },
    '& input:checked + span': { backgroundColor: '#008715' },
    '& input:checked + span::before': { transform: 'translateX(20px)' },
  });

  readonly cardClass = css({
    border: '1px solid #ddd',
    borderRadius: '4px',
    overflow: 'hidden',
  });

  readonly sectionHeaderClass = css({
    backgroundColor: '#f5f5f5',
    padding: '10px 16px',
    borderBottom: '1px solid #ddd',
  });

  readonly dividerClass = css({
    borderTop: '1px solid #eee',
    margin: '0',
  });

  readonly tableClass = css({
    width: '100%',
    borderCollapse: 'collapse',
    fontFamily: 'inherit',
    '& th': {
      textAlign: 'left',
      padding: '8px 12px',
      fontSize: '0.875rem',
      fontWeight: 600,
      borderBottom: '1px solid #ddd',
    },
    '& td': {
      padding: '6px 12px',
      verticalAlign: 'middle',
    },
  });

  readonly emailInputClass = css({
    width: '100%',
    padding: '6px 8px',
    border: '1px solid #b3b3b3',
    borderRadius: '4px',
    fontSize: '0.95rem',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  });

  readonly emailFieldClass = css({
    position: 'relative',
    width: '100%',
  });

  readonly suggestionsDropdownClass = css({
    position: 'absolute',
    top: 'calc(100% + 2px)',
    left: 0,
    right: 0,
    zIndex: 20,
    maxHeight: '240px',
    overflowY: 'auto',
    background: '#fff',
    border: '1px solid #ddd',
    borderRadius: '4px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
  });

  readonly suggestionItemClass = css({
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    width: '100%',
    padding: '8px 12px',
    border: 'none',
    borderBottom: '1px solid #f0f0f0',
    background: 'none',
    textAlign: 'left',
    cursor: 'pointer',
    fontFamily: 'inherit',
    '&:hover': { backgroundColor: '#f5f7fa' },
    '&:last-child': { borderBottom: 'none' },
  });

  readonly suggestionEmailClass = css({
    fontSize: '0.9rem',
    fontWeight: 500,
    color: '#1a1a1a',
  });

  readonly suggestionNameClass = css({
    fontSize: '0.75rem',
    color: '#666',
  });

  readonly suggestionStatusClass = css({
    padding: '8px 12px',
    fontSize: '0.8rem',
    color: '#666',
  });

  readonly deleteButtonClass = css({
    background: 'none',
    border: '1px solid #ED2121',
    borderRadius: '4px',
    color: '#ED2121',
    cursor: 'pointer',
    padding: '4px 8px',
    fontFamily: 'inherit',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  });

  readonly addApproverButtonClass = css({
    margin: '10px 12px',
    padding: '6px 14px',
    border: '1px solid #1976d2',
    borderRadius: '20px',
    background: 'none',
    color: '#1976d2',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: 500,
    fontFamily: 'inherit',
  });
}